<template>
  <div class="schedule-page">
    <!-- Header Controls -->
    <div class="controls-bar elevation-2">
      <div class="controls-inner">
        <div class="controls-left">
          <v-btn icon variant="text" class="nav-btn" @click="navigateDate(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <div class="date-display" @click="showDatePicker = true">
            <v-icon size="18" class="me-1">mdi-calendar</v-icon>
            <span class="date-text">{{ formattedDate }}</span>
          </div>
          <v-btn icon variant="text" class="nav-btn" @click="navigateDate(1)">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn
            variant="tonal"
            size="small"
            class="today-btn"
            @click="goToday"
          >
            Today
          </v-btn>
        </div>
        <div class="controls-right">
          <v-select
            v-model="branchFilter"
            :items="branchs"
            label="Branch"
            variant="outlined"
            density="compact"
            hide-details
            min-width="180"
            prepend-inner-icon="mdi-map-marker-outline"
          />
        </div>
      </div>
    </div>

    <!-- Date Picker Dialog -->
    <v-dialog v-model="showDatePicker" max-width="360">
      <v-card>
        <v-date-picker
          v-model="selectedDate"
          hide-header
          @update:model-value="showDatePicker = false"
        />
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay
      :model-value="isLoading"
      class="align-center justify-center"
      contained
    >
      <v-progress-circular color="primary" size="56" indeterminate />
    </v-overlay>

    <!-- Empty State -->
    <transition name="fade">
      <div v-if="!isLoading && facilityGroups.length === 0" class="empty-state">
        <v-icon size="72" color="grey-lighten-1"
          >mdi-calendar-blank-outline</v-icon
        >
        <p class="empty-title">No bookings on this day</p>
        <p class="empty-subtitle">{{ formattedDate }}</p>
      </div>
    </transition>

    <!-- Schedule Grid -->
    <transition name="fade">
      <div
        v-if="!isLoading && facilityGroups.length > 0"
        class="schedule-container"
      >
        <!-- Timeline Ruler -->
        <div class="timeline-ruler">
          <div class="ruler-label-spacer" />
          <div class="ruler-hours">
            <div
              v-for="hour in timelineHours"
              :key="hour"
              class="ruler-hour-cell"
              :style="{ width: hourWidth + 'px' }"
            >
              <span class="ruler-hour-text">{{ formatHour(hour) }}</span>
            </div>
          </div>
        </div>

        <!-- Facility Rows -->
        <div class="facility-list">
          <div
            v-for="group in facilityGroups"
            :key="group.facilityNo"
            class="facility-row"
          >
            <!-- Facility Label -->
            <div class="facility-label">
              <div class="facility-badge">
                <span class="facility-badge-no">No.{{ group.facilityNo }}</span>
              </div>
            </div>

            <!-- Booking Bars -->
            <div class="facility-track" ref="trackRef">
              <!-- Hour grid lines -->
              <div class="hour-grid">
                <div
                  v-for="hour in timelineHours"
                  :key="hour"
                  class="hour-cell"
                  :style="{ width: hourWidth + 'px' }"
                />
              </div>

              <!-- Booking blocks -->
              <div
                v-for="booking in group.bookings"
                :key="booking.id + '-' + booking.slotIdx"
                class="booking-block"
                :style="getBlockStyle(booking)"
                @click="onBookingClick(booking)"
              >
                <div class="booking-block-inner">
                  <div class="booking-time">
                    {{ booking.startFormatted }} – {{ booking.endFormatted }}
                  </div>
                  <div class="booking-name">{{ booking.attendee }}</div>
                  <div class="booking-resource">{{ booking.resource }}</div>
                </div>
              </div>

              <!-- Current time indicator (only for today) -->
              <div
                v-if="isToday"
                class="current-time-line"
                :style="{ left: currentTimeLeft + 'px' }"
              />
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Booking Detail Bottom Sheet -->
    <v-bottom-sheet v-model="isDetailShown" inset>
      <v-card class="detail-sheet pa-5">
        <div class="d-flex align-start">
          <div class="flex-grow-1">
            <div class="detail-facility-no">
              Facility No. {{ detailBooking.facilityNo }}
            </div>
            <h3 class="detail-name">{{ detailBooking.attendee }}</h3>
            <div class="detail-resource">
              <v-icon size="16" class="me-1">mdi-land-plots</v-icon>
              {{ detailBooking.resource }}
            </div>
            <div class="detail-time mt-2">
              <v-icon size="16" class="me-1">mdi-clock-outline</v-icon>
              {{ detailBooking.startFormatted }} –
              {{ detailBooking.endFormatted }}
            </div>
            <div class="detail-date mt-1">
              <v-icon size="16" class="me-1">mdi-calendar</v-icon>
              {{ formattedDate }}
            </div>
          </div>
          <v-btn icon variant="text" @click="isDetailShown = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script setup>
import { authAxios } from "@/util/Axios";
import BackendConnector from "@/util/BackendConnector";
import moment from "moment";
import { ref, reactive, computed, watch, onMounted, onUnmounted } from "vue";

const axios = authAxios();

// ─── State ────────────────────────────────────────────────────────────────────
const selectedDate = ref(new Date());
const branchFilter = ref("all");
const branchs = reactive([{ title: "All", value: "all" }]);
const isLoading = ref(false);
const showDatePicker = ref(false);

const rawBookings = ref([]);

const isDetailShown = ref(false);
const detailBooking = reactive({});

// ─── Timeline Config ───────────────────────────────────────────────────────────
const TIME_START = 6; // 06:00
const TIME_END = 23; // 23:00
const hourWidth = 80; // px per hour

const timelineHours = computed(() => {
  const hours = [];
  for (let h = TIME_START; h <= TIME_END; h++) hours.push(h);
  return hours;
});

// ─── Computed ─────────────────────────────────────────────────────────────────
const formattedDate = computed(() =>
  moment(selectedDate.value).format("ddd, D MMM YYYY")
);

const isToday = computed(() =>
  moment(selectedDate.value).isSame(moment(), "day")
);

const currentTimeLeft = computed(() => {
  const now = moment();
  const minutesFromStart = (now.hours() - TIME_START) * 60 + now.minutes();
  return (minutesFromStart / 60) * hourWidth;
});

/**
 * Group bookings by occupation_facility (facility number).
 * Each booking may have multiple time slots (start_time[], end_time[]).
 * We expand them into individual slot entries for display.
 */
const facilityGroups = computed(() => {
  const map = new Map();

  rawBookings.value.forEach((booking) => {
    const slots = booking.start_time || [];
    const facilities = Array.isArray(booking.occupation_facility)
      ? booking.occupation_facility
      : booking.occupation_facility
      ? [booking.occupation_facility]
      : [];

    facilities.forEach((facilityNo) => {
      slots.forEach((_, idx) => {
        const sTime = moment(booking.start_time[idx], "HH:mm:ss");
        const eTime = moment(booking.end_time[idx], "HH:mm:ss");

        const entry = {
          id: booking.id,
          slotIdx: `${facilityNo}-${idx}`,
          facilityNo: facilityNo,
          attendee: booking.attendee,
          resource: booking.resource,
          startFormatted: sTime.format("HH:mm"),
          endFormatted: eTime.format("HH:mm"),
          startMinutes: sTime.hours() * 60 + sTime.minutes(),
          endMinutes: eTime.hours() * 60 + eTime.minutes(),
        };

        if (!map.has(facilityNo)) map.set(facilityNo, []);
        map.get(facilityNo).push(entry);
      });
    });
  });

  // Sort by facility number, return as array
  return Array.from(map.entries())
    .sort((a, b) => {
      const na = parseInt(a[0]) || 0;
      const nb = parseInt(b[0]) || 0;
      return na - nb;
    })
    .map(([facilityNo, bookings]) => ({ facilityNo, bookings }));
});

// ─── Methods ──────────────────────────────────────────────────────────────────
function formatHour(hour) {
  return moment({ hour }).format("HH:mm");
}

function getBlockStyle(booking) {
  const startOffset = booking.startMinutes - TIME_START * 60;
  const duration = booking.endMinutes - booking.startMinutes;

  const left = (startOffset / 60) * hourWidth;
  const width = Math.max((duration / 60) * hourWidth - 4, 20);

  return {
    left: left + "px",
    width: width + "px",
  };
}

function onBookingClick(booking) {
  Object.assign(detailBooking, booking);
  isDetailShown.value = true;
}

function navigateDate(delta) {
  const d = moment(selectedDate.value).add(delta, "days").toDate();
  selectedDate.value = d;
}

function goToday() {
  selectedDate.value = new Date();
}

// ─── Data Loading ─────────────────────────────────────────────────────────────
async function loadSchedule() {
  isLoading.value = true;
  const dateStr = moment(selectedDate.value).format("YYYY-MM-DD");
  try {
    const response = await axios({
      method: "GET",
      url: `${process.env.VUE_APP_BACKEND_URL}/api/facility_booking/calendar`,
      params: {
        start_date: dateStr,
        end_date: dateStr,
        branch: branchFilter.value,
      },
    });
    rawBookings.value = response.data || [];
  } catch {
    rawBookings.value = [];
  } finally {
    isLoading.value = false;
  }
}

// ─── Watchers ─────────────────────────────────────────────────────────────────
watch([selectedDate, branchFilter], () => {
  loadSchedule();
});

// ─── Current time ticker ──────────────────────────────────────────────────────
let ticker = null;
const _tick = ref(0);

onMounted(async () => {
  await BackendConnector.loadBranch()
    .then((res) => {
      res.data.data.forEach((b) => {
        branchs.push({ title: b.name, value: b.id });
      });
    })
    .catch(() => {});

  loadSchedule();

  ticker = setInterval(() => {
    _tick.value++;
  }, 60_000);
});

onUnmounted(() => {
  clearInterval(ticker);
});
</script>

<style scoped>
/* ── Page Layout ─────────────────────────────────────────────────── */
.schedule-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f7fa;
  overflow: hidden;
  position: relative;
}

/* ── Controls Bar ─────────────────────────────────────────────────── */
.controls-bar {
  background: #ffffff;
  z-index: 10;
  flex-shrink: 0;
}
.controls-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  gap: 12px;
  flex-wrap: wrap;
}
.controls-left {
  display: flex;
  align-items: center;
  gap: 4px;
}
.controls-right {
  display: flex;
  align-items: center;
}

.nav-btn {
  color: #455a64;
}
.date-display {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 8px;
  transition: background 0.18s;
  user-select: none;
}
.date-display:hover {
  background: #f0f4f8;
}
.date-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #263238;
  letter-spacing: 0.01em;
}
.today-btn {
  margin-left: 6px;
  text-transform: none;
  font-weight: 600;
  font-size: 0.8rem;
  color: #1565c0;
}

/* ── Empty State ──────────────────────────────────────────────────── */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #90a4ae;
}
.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #78909c;
}
.empty-subtitle {
  font-size: 0.85rem;
  margin: 0;
  color: #b0bec5;
}

/* ── Schedule Container ───────────────────────────────────────────── */
.schedule-container {
  flex: 1;
  overflow: auto;
  padding: 16px 0 24px;
}

/* ── Timeline Ruler ───────────────────────────────────────────────── */
.timeline-ruler {
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 5;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e6ed;
  padding-bottom: 4px;
  margin-bottom: 4px;
}
.ruler-label-spacer {
  width: 88px;
  flex-shrink: 0;
}
.ruler-hours {
  display: flex;
  overflow: visible;
}
.ruler-hour-cell {
  flex-shrink: 0;
  text-align: left;
  padding-left: 4px;
}
.ruler-hour-text {
  font-size: 0.72rem;
  font-weight: 600;
  color: #90a4ae;
  letter-spacing: 0.03em;
}

/* ── Facility Row ─────────────────────────────────────────────────── */
.facility-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.facility-row {
  display: flex;
  align-items: center;
  min-height: 60px;
}

/* Facility Label */
.facility-label {
  width: 88px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 0 6px;
}
.facility-badge {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%);
  border-radius: 10px;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 8px rgba(21, 101, 192, 0.25);
  min-width: 68px;
}
.facility-badge-no {
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

/* Facility Track */
.facility-track {
  flex: 1;
  position: relative;
  height: 52px;
  overflow: visible;
}

/* Hour grid lines */
.hour-grid {
  display: flex;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}
.hour-cell {
  flex-shrink: 0;
  border-left: 1px solid #e0e6ed;
  height: 100%;
  box-sizing: border-box;
}

/* Booking Block */
.booking-block {
  position: absolute;
  top: 4px;
  height: 44px;
  border-radius: 8px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 1.5px solid #64b5f6;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.18s, transform 0.15s;
  z-index: 2;
}
.booking-block:hover {
  box-shadow: 0 4px 16px rgba(21, 101, 192, 0.22);
  transform: translateY(-1px);
}
.booking-block-inner {
  padding: 3px 7px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}
.booking-time {
  font-size: 0.7rem;
  font-weight: 700;
  color: #1565c0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.booking-name {
  font-size: 0.72rem;
  font-weight: 600;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.booking-resource {
  font-size: 0.65rem;
  color: #546e7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Current time indicator */
.current-time-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e53935;
  border-radius: 2px;
  z-index: 4;
}
.current-time-line::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e53935;
}

/* ── Detail Sheet ─────────────────────────────────────────────────── */
.detail-sheet {
  border-radius: 16px 16px 0 0 !important;
}
.detail-facility-no {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1565c0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
}
.detail-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #263238;
  margin: 0 0 4px;
}
.detail-resource,
.detail-time,
.detail-date {
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  color: #546e7a;
}

/* ── Transitions ──────────────────────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
